import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RetexViewPage() {
  const { retexId } = useParams();
  const [retex, setRetex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchRetex = async () => {
      try {
        const response = await fetch(`${API_URL}/api/retex/view/${retexId}`);
        if (!response.ok) throw new Error('RETEX non trouvé');
        const data = await response.json();
        setRetex(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRetex();
  }, [retexId]);

  const handlePlayAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement du RETEX...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-500 rounded-xl p-8 text-center">
          <div className="text-red-400 text-xl mb-2">Erreur</div>
          <div className="text-white">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-800 to-blue-600 rounded-t-2xl p-8 text-center text-white">
          <div className="text-sm uppercase tracking-wider opacity-70 mb-2">RETEX Formation IA</div>
          <h1 className="text-3xl font-bold mb-2">{retex.client}</h1>
          <div className="text-blue-200">{retex.location}</div>
        </div>

        {/* Audio Player Section */}
        {retex.has_audio && (
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
            <div className="flex items-center justify-center gap-6">
              <button
                onClick={handlePlayAudio}
                className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                data-testid="play-audio-btn"
              >
                {isPlaying ? (
                  <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
              <div className="text-white">
                <div className="text-lg font-semibold">Écouter le RETEX</div>
                <div className="text-purple-200 text-sm">Voix : Lucie - Académie IA</div>
              </div>
            </div>
            <audio
              ref={audioRef}
              src={`${API_URL}/api/retex/audio/${retexId}`}
              onEnded={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-b-2xl shadow-2xl">
          
          {/* Recipient Info */}
          <div className="p-6 border-b border-gray-100">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium mb-1">Destinataire</div>
                <div className="text-gray-800">{retex.recipient_email || 'Non spécifié'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 font-medium mb-1">Commanditaire</div>
                <div className="text-gray-800">{retex.sponsor_email || 'Non spécifié'}</div>
              </div>
            </div>
          </div>

          {/* RETEX Content */}
          <div className="p-8">
            {retex.sections && retex.sections.map((section, index) => (
              <div key={index} className="mb-8">
                <h2 className="text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm">
                    {index + 1}
                  </span>
                  {section.title}
                </h2>
                <div className="bg-gray-50 rounded-lg p-4 text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {section.content}
                </div>
              </div>
            ))}

            {/* Tools Recommended */}
            {retex.tools && retex.tools.length > 0 && (
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                <h3 className="text-lg font-bold text-blue-900 mb-4">🛠️ Outils IA Recommandés</h3>
                <div className="flex flex-wrap gap-2">
                  {retex.tools.map((tool, index) => (
                    <span key={index} className="bg-white px-4 py-2 rounded-full text-blue-700 shadow-sm">
                      ✨ {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-6 rounded-b-2xl text-center">
            <div className="text-2xl mb-2">💙</div>
            <div className="text-lg font-semibold text-blue-900">Lucie</div>
            <div className="text-gray-500 text-sm">Cyber-Assistante • Académie IA</div>
            <div className="text-gray-400 text-xs mt-2">
              Ce rapport a été généré avec l'assistance de l'Intelligence Artificielle
            </div>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <a 
            href="https://academie-ia.fr" 
            className="text-blue-300 hover:text-white transition-colors"
          >
            ← Retour à Académie IA
          </a>
        </div>
      </div>
    </div>
  );
}
